import type { CityConfig } from "../city-template/types";

export const warrenConfig: CityConfig = {
  city: "Warren",
  state: "Ohio",
  stateAbbr: "OH",
  slug: "warren",
  route: "/cities/warren-oh",
  title: "Emergency Passport Warren, OH | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Warren, OH — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Trumbull County and the Mahoning Valley. Get your passport fast!",
  h1: "Emergency & Expedited Passport Services in Warren, OH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Warren and Trumbull County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Warren%20Ohio%20downtown%20cityscape%20at%20golden%20hour%20with%20historic%20courthouse%20and%20civic%20buildings%2C%20warm%20amber%20light%20on%20brick%20architecture%2C%20tree-lined%20streets%20with%20autumn%20foliage%2C%20small%20town%20American%20charm%20in%20the%20Rust%20Belt%20region%2C%20professional%20cityscape%20photography%20with%20vibrant%20warm%20colors%20and%20clear%20sky&width=1800&height=700&seq=warren-oh-hero-001&orientation=landscape",
  heroImageAlt: "Warren Ohio downtown with historic courthouse at golden hour",
  geoPosition: "41.2376;-80.8184",
  geoPlacename: "Warren, Ohio",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 375 miles from Warren via I-80",
  },
  nearbyCitiesZips: [
    { name: "Warren", zips: "44481, 44482, 44483, 44484, 44485, 44486" },
    { name: "Niles", zips: "44446" },
    { name: "Howland", zips: "44484" },
    { name: "Cortland", zips: "44410" },
    { name: "Girard", zips: "44420" },
    { name: "Hubbard", zips: "44425" },
    { name: "Lordstown", zips: "44481" },
    { name: "Newton Falls", zips: "44444" },
    { name: "Leavittsburg", zips: "44430" },
    { name: "Bristolville", zips: "44402" },
    { name: "Mecca", zips: "44410" },
    { name: "Vienna", zips: "44473" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Warren departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-global-line", title: "Mahoning Valley Coverage", desc: "Serving Trumbull County and the greater Youngstown area" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Youngstown", route: "/cities/youngstown" },
    { name: "Fast passport renewal in Cleveland, OH", route: "/cities/cleveland" },
    { name: "Emergency passport help in Akron, OH", route: "/cities/akron" },
    { name: "Urgent passport services in Canton, OH", route: "/cities/canton" },
    { name: "Same-day passport help in Mentor, OH", route: "/cities/mentor" },
    { name: "Passport courier services in Lorain, OH", route: "/cities/lorain" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Warren remotely via secure courier. We do not have a walk-in office in Warren — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Trumbull County and the Mahoning Valley travelers.",
  fpcOfficeDistance: "Serving all of Trumbull County and the Mahoning Valley",
  serviceAreaIntro: "From downtown Warren to Niles, Howland, and Cortland — Fast Passport Center serves the entire Trumbull County and Mahoning Valley region. We regularly help travelers from the Warren area get their passports fast.",
  nearbyCitiesIntro: "We serve all Warren neighborhoods and the surrounding Trumbull County region including Niles, Howland, Cortland, Girard, Hubbard, Lordstown, Newton Falls, Leavittsburg, Bristolville, Mecca, and Vienna. If you are anywhere in the Mahoning Valley, we have you covered.",
  howItWorksIntro: "Most Warren travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Warren travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Warren and Trumbull County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Warren? Here are the most common ones we hear from Trumbull County travelers.",
  statePageRoute: "/states/ohio",
  statePageName: "Ohio",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Warren international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Robert M.",
      location: "Warren, OH",
      rating: 5,
      text: "My passport expired just before a business trip to Germany. Fast Passport Center had my renewal ready in 5 days. They guided me through every step and made it stress-free.",
      service: "Passport Renewal",
    },
    {
      name: "Angela K.",
      location: "Niles, OH",
      rating: 5,
      text: "I thought I'd have to drive to Cleveland or Chicago for an emergency passport. Fast Passport Center handled everything remotely — got mine in 48 hours. Saved me a long trip.",
      service: "Emergency Passport",
    },
    {
      name: "Michael T.",
      location: "Cortland, OH",
      rating: 5,
      text: "Family cruise to the Caribbean and my daughter's passport was expired. They found us the nearest acceptance facility and had her passport in 4 days. Truly amazing service.",
      service: "Child Passport",
    },
    {
      name: "Lisa W.",
      location: "Howland, OH",
      rating: 5,
      text: "Last-minute business trip to Japan with only 6 days notice. Fast Passport Center delivered my passport in 3 business days. Professional and worth every penny.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "warren-1",
      name: "Warren Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "360 High St NW",
      city: "Warren",
      state: "OH",
      zip: "44481",
      phone: "(330) 394-0431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Warren",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Main facility serving Warren and Trumbull County. High volume — appointments recommended. Multiple windows available.",
      icon: "ri-mail-line",
    },
    {
      id: "warren-2",
      name: "Niles Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10 W State St",
      city: "Niles",
      state: "OH",
      zip: "44446",
      phone: "(330) 652-3501",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Niles, south of Warren",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Niles and southern Trumbull County residents. Less crowded than Warren main office.",
      icon: "ri-mail-line",
    },
    {
      id: "warren-3",
      name: "Trumbull County Clerk of Courts",
      type: "Court Clerk Passport Acceptance",
      address: "161 High St NW",
      city: "Warren",
      state: "OH",
      zip: "44481",
      phone: "(330) 675-2557",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "Trumbull County Courthouse",
      website: "https://www.trumbullcountyoh.gov/",
      websiteLabel: "Trumbull County Courts",
      notes: "Accepts passport applications. Good alternative to post office with extended hours and appointment availability.",
      icon: "ri-government-line",
    },
    {
      id: "warren-4",
      name: "Girard Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "101 N State St",
      city: "Girard",
      state: "OH",
      zip: "44420",
      phone: "(330) 545-2521",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Girard, west of Warren",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves western Trumbull County. Ample parking. Call for appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "warren-5",
      name: "Hubbard Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "131 N Main St",
      city: "Hubbard",
      state: "OH",
      zip: "44425",
      phone: "(330) 534-2521",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Hubbard, east of Warren",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Hubbard and eastern Trumbull County residents. Appointments required.",
      icon: "ri-mail-line",
    },
    {
      id: "warren-6",
      name: "Newton Falls Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "19 W Broad St",
      city: "Newton Falls",
      state: "OH",
      zip: "44444",
      phone: "(330) 872-2521",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Newton Falls, south of Warren",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves southern Trumbull County. Call for hours and appointment.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 6 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Warren photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Trumbull County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Warren applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Trumbull County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Warren travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Warren." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Warren address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Warren before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Warren for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 6 hour drive to Chicago." },
    { q: "How fast can I renew my passport in Warren?", a: "Fast Passport Center offers fast passport renewal in Warren in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "Can I get a same-day passport appointment near Warren?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — approximately 375 miles from Warren — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Trumbull County residents." },
    { q: "Can I expedite a child passport in Warren?", a: "Yes. Fast Passport Center expedites child passports in Warren for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Trumbull County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Warren with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "What is the closest passport agency to Warren?", a: "The nearest regional passport agency to Warren is the Chicago Passport Agency, approximately 375 miles away via I-80. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Warren without any in-person agency visit required." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "How do I find a passport acceptance facility near Warren?", a: "Trumbull County has several passport acceptance facilities including the Warren Main Post Office, Niles Post Office, Girard Post Office, Hubbard Post Office, Newton Falls Post Office, and the Trumbull County Clerk of Courts. We can help you locate the most convenient facility and schedule your appointment." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Warren", address: "2915 Niles Cortland Rd SE, Warren, OH 44484", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Warren", address: "2800 Parkman Rd NW, Warren, OH 44485", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart Supercenter — Warren", address: "2895 Niles Cortland Rd SE, Warren, OH 44484", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Warren", address: "1350 W Market St, Warren, OH 44485", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Niles", address: "5555 Youngstown Warren Rd, Niles, OH 44446", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Girard", address: "1 N State St, Girard, OH 44420", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Cortland", address: "2555 Niles Cortland Rd SE, Cortland, OH 44410", hours: "Mon–Sun: 6AM–11PM" },
    { name: "CVS Pharmacy — Hubbard", address: "101 W Liberty St, Hubbard, OH 44425", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "Trumbull County BMV — Warren",
      address: "4260 Mahoning Ave NW, Warren, OH 44483",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver's license and REAL ID services. Appointment strongly recommended.",
      featured: true,
    },
    {
      name: "Niles BMV License Agency",
      address: "20 W Federal St, Niles, OH 44446",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Alternative for southern Trumbull County residents. REAL ID available.",
      featured: false,
    },
    {
      name: "Girard License Agency",
      address: "121 W Main St, Girard, OH 44420",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Convenient for Girard and western Trumbull County residents.",
      featured: false,
    },
    {
      name: "AAA — Warren",
      address: "4220 Mahoning Ave NW, Warren, OH 44483",
      hours: "Mon–Fri: 8AM–5:30PM, Sat: 9AM–12PM",
      note: "AAA members can renew licenses and access passport photo services at this location.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "YNG",
      name: "Youngstown-Warren Regional Airport",
      distance: "15 miles from Warren",
      address: "1453 Youngstown Warren Regional Airport, Vienna, OH 44473",
      note: "Regional airport serving the Mahoning Valley. Allegiant offers flights to Florida and seasonal destinations. No major international flights.",
      flightUrl: "https://www.yngairport.com/",
      flightLabel: "YNG Airport",
      featured: true,
    },
    {
      code: "CLE",
      name: "Cleveland Hopkins International Airport",
      distance: "65 miles north of Warren",
      address: "5300 Riverside Dr, Cleveland, OH 44135",
      note: "Major international hub with flights to Europe, Canada, Mexico, and the Caribbean. Best option for Warren international travelers.",
      flightUrl: "https://www.clevelandairport.com/",
      flightLabel: "Cleveland Airport",
      featured: false,
    },
    {
      code: "PIT",
      name: "Pittsburgh International Airport",
      distance: "70 miles southeast of Warren",
      address: "1000 Airport Blvd, Pittsburgh, PA 15231",
      note: "Alternative major international hub. Additional international routes and often less crowded than Cleveland.",
      flightUrl: "https://www.flypittsburgh.com/",
      flightLabel: "Pittsburgh Airport",
      featured: false,
    },
  ],
};